/**
 * Work Arrangement API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  WorkArrangement,
  WorkArrangementListResponse,
  WorkArrangementResponse,
  WorkArrangementCreateInput,
  WorkArrangementUpdateInput,
} from '../types/work-arrangement';

interface ListWorkArrangementsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class WorkArrangementService {
  /**
   * List work arrangements with pagination.
   */
  async listWorkArrangements(
    workspaceId: string,
    idToken: string,
    options: ListWorkArrangementsOptions = {}
  ): Promise<WorkArrangementListResponse> {
    const { page = 1, size = 50, sortBy = 'name', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<WorkArrangementListResponse>(`/work-arrangements`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list work arrangements');
    }

    return response.data;
  }

  /**
   * Get a single work arrangement by ID.
   */
  async getWorkArrangement(
    workspaceId: string,
    workArrangementId: string,
    idToken: string
  ): Promise<WorkArrangement> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<WorkArrangementResponse>(
      `/work-arrangements/${workArrangementId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get work arrangement');
    }

    return response.data.data;
  }

  /**
   * Create a new work arrangement.
   */
  async createWorkArrangement(
    workspaceId: string,
    idToken: string,
    data: WorkArrangementCreateInput
  ): Promise<WorkArrangement> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<WorkArrangementResponse>(`/work-arrangements`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create work arrangement');
    }

    return response.data.data;
  }

  /**
   * Bulk create work arrangements; duplicates skipped silently.
   */
  async bulkCreateWorkArrangements(
    workspaceId: string,
    idToken: string,
    names: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/work-arrangements/bulk`,
      { names },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create work arrangements');
    }

    return response.data.data;
  }

  /**
   * Update a work arrangement.
   */
  async updateWorkArrangement(
    workspaceId: string,
    workArrangementId: string,
    idToken: string,
    data: WorkArrangementUpdateInput
  ): Promise<WorkArrangement> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<WorkArrangementResponse>(
      `/work-arrangements/${workArrangementId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update work arrangement');
    }

    return response.data.data;
  }

  /**
   * Delete a work arrangement.
   */
  async deleteWorkArrangement(
    workspaceId: string,
    workArrangementId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/work-arrangements/${workArrangementId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const workArrangementService = new WorkArrangementService();
export default workArrangementService;
